import { gradeAppealNotificationContextSchema } from '@breakoutlearning/firebase-repository/firestore/schemas'
import type { GradeAppealNotificationEvent } from '@breakoutlearning/firebase-repository/firestore/schemas'
import type { NotificationGroup } from '@breakoutlearning/firebase-repository/types'
import { UserProfileNotificationType } from '@breakoutlearning/firebase-repository/types'
import classNames from 'classnames'
import { ChatBubbleIcon } from 'components/icons/ChatBubble'
import { CheckIcon } from 'components/icons/Check'
import { Close } from 'components/icons/Close'
import { PercentageIcon } from 'components/icons/Percentage'
import { useBreakoutUser } from 'hooks/profile'
import { useRootStore } from 'hooks/rootStore'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import { RelativeTime } from './shared'

const eventStyles: Record<
  GradeAppealNotificationEvent,
  { icon: string; score: string }
> = {
  submitted: {
    icon: 'bg-fixed-light-yellow',
    score: '',
  },
  accepted: {
    icon: 'bg-fixed-light-mint text-core-success',
    score: 'bg-fixed-light-mint text-core-success',
  },
  'partially-accepted': {
    icon: 'bg-fixed-light-teal text-fixed-teal',
    score: 'bg-fixed-light-teal text-fixed-teal',
  },
  declined: {
    icon: 'bg-fixed-light-rust text-fixed-rust',
    score: 'bg-fixed-light-rust text-fixed-rust',
  },
}

function GradeAppealEventIcon({
  event,
}: {
  event: GradeAppealNotificationEvent
}) {
  if (event === 'submitted') {
    return (
      <span
        aria-hidden="true"
        className="size-2 rounded-full bg-fixed-accent-color"
      />
    )
  }

  if (event === 'accepted') {
    return <CheckIcon aria-hidden="true" size={27} strokeWidth={3} />
  }

  if (event === 'partially-accepted') {
    return <PercentageIcon aria-hidden="true" size={24} strokeWidth={2.5} />
  }

  return <Close aria-hidden="true" size={24} />
}

export const GradeAppealDecisionNotification = observer(
  function GradeAppealDecisionNotification({
    notificationGroup,
    onDismiss,
  }: {
    notificationGroup: NotificationGroup
    onDismiss: () => void
  }) {
    const { tt } = useTranslationTyped()
    const rootStore = useRootStore()
    const breakoutUser = useBreakoutUser()
    const contextResult = gradeAppealNotificationContextSchema.safeParse(
      notificationGroup.context
    )

    if (
      notificationGroup.type !== UserProfileNotificationType.GradeAppeal ||
      !contextResult.success
    ) {
      return <div className="px-5">{notificationGroup.subject}</div>
    }

    const context = contextResult.data
    const notificationsCubit = breakoutUser.notifications
    const section = notificationsCubit.sections.get(context.sectionId)
    const slideDeck = notificationsCubit.slideDecks.get(context.slideDeckId)
    const note =
      notificationGroup.notifications.at(0)?.data.notificationTextBody

    const title = {
      submitted: notificationGroup.subject,
      accepted: tt.notifications.grade_appeal_decision.accepted(),
      'partially-accepted':
        tt.notifications.grade_appeal_decision.partially_accepted(),
      declined: tt.notifications.grade_appeal_decision.declined(),
    }[context.event]
    const styles = eventStyles[context.event]

    return (
      <button
        className="focus-ring-floating group mx-3 my-2 block w-auto rounded-2xl bg-fixed-eggshell px-3 py-3 text-left transition-shadow hover:shadow-md"
        type="button"
        onClick={() => {
          notificationsCubit.markGroupAsRead(notificationGroup)
          onDismiss()
          rootStore.navigateTo(
            'assignmentWithRoom',
            {
              assignmentId: context.assignmentId,
              sectionId: context.sectionId,
              roomStateId: context.roomId,
            },
            { action: 'viewResults' }
          )
        }}
      >
        <div className="flex items-start gap-2">
          <span
            aria-hidden="true"
            className="mt-3 size-2 shrink-0 rounded-full bg-fixed-accent-color"
          />
          <span
            className={classNames(
              'flex size-9 shrink-0 items-center justify-center rounded-full',
              styles.icon
            )}
          >
            <GradeAppealEventIcon event={context.event} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <strong className="text-body-large leading-tight text-on-surface">
                {title}
              </strong>
              <span className="shrink-0 pt-0.5">
                <RelativeTime timestamp={notificationGroup.timestamp} />
              </span>
            </div>
            {slideDeck || section ? (
              <div className="mt-2 flex items-center gap-2 rounded-xl bg-surface-dim px-2 py-2 text-body-medium font-semibold text-on-surface">
                <ChatBubbleIcon
                  aria-hidden="true"
                  className="shrink-0 text-fixed-teal"
                  size={18}
                />
                <span className="truncate">
                  {slideDeck?.data.slideDeckName}
                  {slideDeck && section ? ' · ' : null}
                  {section?.data.className}
                </span>
              </div>
            ) : null}
            {context.event !== 'submitted' && context.finalScore ? (
              <div
                className={classNames(
                  'mt-2 inline-flex rounded-full px-2 py-0.5 text-label-medium',
                  styles.score
                )}
              >
                {tt.notifications.grade_appeal_decision.final_grade({
                  score: context.finalScore,
                })}
              </div>
            ) : null}
            {context.event !== 'submitted' && note ? (
              <div className="mt-3 border-l-2 border-outline-light pl-2 text-body-medium leading-snug text-on-surface-var">
                {note}
              </div>
            ) : null}
          </div>
        </div>
      </button>
    )
  }
)
